/**
 * Which plan a client is on today, read off every payment request raised
 * against them.
 *
 * There is no "current plan" endpoint: a plan is what a *paid* request bought,
 * and the request carries the stretch it opened — `engagement.start_date` to
 * `engagement.end_date`, the same days the flat `ends_on` repeats. So the
 * banner above the meal plan list is a reading of the payment history, and this
 * is where that reading is done.
 *
 * Three answers, because there are three things a dietitian needs to see at a
 * glance:
 *
 *   `active`   — a stretch covering today. What it is, and how long is left.
 *   `expired`  — none covering today, but one has run out. The client is
 *                between plans, whether or not a next one has been bought.
 *   `none`     — nothing has ever run. A client who has not been sold a plan.
 *
 * Days are compared as days: a plan that runs out today has run out at the end
 * of today, not at the moment the page was opened.
 */

import { atMidnight, parseDay } from "./clientMapping";
import type {
  ClientEngagement,
  ClientPayment,
  ClientPlan,
} from "./types/clients.types";

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** One stretch of plan, with its days read out of the row that bought it. */
interface Stretch {
  payment: ClientPayment;
  /** `null` where the backend opened the engagement without a start date. */
  start: Date | null;
  /** The last day of the stretch, as a day and as the string it arrived as. */
  end: Date;
  endsOn: string;
}

/**
 * The stretch a request opened, or `null` where it opened none — an unpaid
 * request has bought nothing, and a cancelled one bought nothing that stands.
 *
 * Both are read off the dates rather than off the status: a row without an end
 * date has no stretch to place on a calendar whatever its status says, which
 * also keeps a backend state the app has never heard of from inventing one.
 */
const stretchOf = (payment: ClientPayment): Stretch | null => {
  if (payment.status === "Cancelled") return null;

  const end = parseDay(payment.endsOn);
  if (!end || !payment.endsOn) return null;

  return {
    payment,
    start: parseDay(payment.startsOn),
    end,
    endsOn: payment.endsOn,
  };
};

/** What the banner names it: the plan as sold, and the day it runs to. */
const engagementOf = ({ payment, endsOn }: Stretch): ClientEngagement => ({
  name: payment.planName,
  duration: payment.duration,
  endsOn,
});

/** Latest end date first — which of several is the one worth showing. */
const byEndDesc = (a: Stretch, b: Stretch) => b.end.getTime() - a.end.getTime();

/**
 * Where this client's subscription stands, out of their whole payment history.
 *
 * `today` is a parameter so the reading can be tested against a fixed day; it
 * is the clock everywhere else.
 */
export const currentPlanOf = (
  payments: ClientPayment[],
  today: Date = new Date()
): ClientPlan => {
  const now = atMidnight(today);
  const stretches = payments
    .map(stretchOf)
    .filter((stretch): stretch is Stretch => stretch !== null);

  // Running today. A stretch the backend opened without a start date counts as
  // already begun — it was opened when the money came in, which is behind us.
  // The one running furthest out wins, so a renewal bought early reads as the
  // plan the client is on rather than the one it replaced.
  const running = stretches
    .filter(({ start, end }) => end >= now && (!start || start <= now))
    .sort(byEndDesc)[0];

  if (running) {
    return {
      state: "active",
      ...engagementOf(running),
      // Whole days to the last one, so a plan on its final day reads as 0
      // rather than as one that has already gone.
      daysLeft: Math.round((running.end.getTime() - now.getTime()) / MS_PER_DAY),
    };
  }

  // Nothing is running. The stretch that ran out most recently is what the
  // banner shows, marked expired — a plan bought to start next month does not
  // change that the client is on nothing today.
  const ended = stretches.filter(({ end }) => end < now).sort(byEndDesc)[0];
  if (ended) return { state: "expired", ...engagementOf(ended) };

  return { state: "none" };
};
